// 📄 services/miEspacioService.ts
// CRUD del módulo personal "Mi Espacio": snippets de código, notas de SAP,
// pendientes y contactos. Cada colección vive bajo usuarios/{uid}/... si es
// contexto Global/Freelance (projectId null), o bajo
// usuarios/{uid}/projectData/{projectId}/... si está asociada a un proyecto
// puntual ("Mi Espacio en el Proyecto") — privado por diseño, cada usuario
// solo ve lo suyo (ver firestore.rules, regla usuarios/{uid}/{document=**}).
import {
  addDoc,
  collection,
  CollectionReference,
  deleteDoc,
  doc,
  DocumentReference,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc
} from "firebase/firestore";
import { db } from '../firebase';
import { Snippet, SnippetData, NotaSap, NotaSapData, Tarea, TareaData, Contacto, ContactoData, Archivo } from '../types/miEspacio.ts';
import { uploadAttachment } from './storageService.ts';

const coleccionRef = (uid: string, coleccion: string, projectId: string | null): CollectionReference =>
  projectId
    ? collection(db, 'usuarios', uid, 'projectData', projectId, coleccion)
    : collection(db, 'usuarios', uid, coleccion);

const docRefFor = (uid: string, coleccion: string, id: string, projectId: string | null): DocumentReference =>
  projectId
    ? doc(db, 'usuarios', uid, 'projectData', projectId, coleccion, id)
    : doc(db, 'usuarios', uid, coleccion, id);

const getItems = async <T,>(uid: string, coleccion: string, projectId: string | null): Promise<(T & { id: string; projectId: string | null })[]> => {
  const q = query(coleccionRef(uid, coleccion, projectId), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as T), projectId }));
};

// Consulta varios contextos a la vez (ej. Global + todos los proyectos del
// usuario, para el filtro "Todos los Proyectos" de Mi Espacio en el Home) y
// devuelve la unión ordenada por fecha de creación descendente.
const getItemsMulti = async <T,>(uid: string, coleccion: string, projectIds: (string | null)[]): Promise<(T & { id: string; projectId: string | null })[]> => {
  const resultados = await Promise.all(projectIds.map((pid) => getItems<T>(uid, coleccion, pid)));
  return resultados.flat().sort((a: any, b: any) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
};

const deleteItem = async (uid: string, coleccion: string, id: string, projectId: string | null): Promise<void> => {
  await deleteDoc(docRefFor(uid, coleccion, id, projectId));
};

// ── Snippets & Código Z ──────────────────────────────────────────────────
export const getSnippets = (uid: string, projectIds: (string | null)[]) => getItemsMulti<SnippetData>(uid, 'snippets', projectIds);

export const saveSnippet = async (uid: string, id: string | null, data: SnippetData, projectId: string | null): Promise<void> => {
  if (!data.titulo.trim() || !data.codigo.trim()) throw new Error("Título y código son obligatorios.");
  const payload = {
    titulo: data.titulo.trim(),
    lenguaje: data.lenguaje.trim(),
    tags: data.tags,
    codigo: data.codigo,
    descripcion: data.descripcion.trim(),
    ubicacionTecnica: (data.ubicacionTecnica || '').trim(),
    updatedAt: serverTimestamp()
  };
  if (id) {
    await updateDoc(docRefFor(uid, 'snippets', id, projectId), payload);
  } else {
    await addDoc(coleccionRef(uid, 'snippets', projectId), { ...payload, createdAt: serverTimestamp() });
  }
};

export const deleteSnippet = (uid: string, id: string, projectId: string | null) => deleteItem(uid, 'snippets', id, projectId);

// ── Notas de SAP y Soluciones ────────────────────────────────────────────
export const getNotasSap = (uid: string, projectIds: (string | null)[]) => getItemsMulti<NotaSapData>(uid, 'notasSap', projectIds);

// Devuelve el id de la nota (el mismo `id` recibido si fue update, o el
// generado por Firestore si fue creación) — lo necesita NotasSapSection
// para escribir el espejo en notasSapCompartidas con el mismo id.
export const saveNotaSap = async (uid: string, id: string | null, data: NotaSapData, projectId: string | null): Promise<string> => {
  if (!data.titulo.trim()) throw new Error("El título / nombre del error es obligatorio.");
  const payload = {
    titulo: data.titulo.trim(),
    numeroONota: data.numeroONota.trim(),
    modulo: data.modulo,
    causa: data.causa.trim(),
    solucion: data.solucion.trim(),
    tipoNota: data.tipoNota || '',
    sistemaAfectado: data.sistemaAfectado || '',
    campoTecnico: (data.campoTecnico || '').trim(),
    tags: data.tags || [],
    versionAmbiente: (data.versionAmbiente || '').trim(),
    fechaVerificacion: data.fechaVerificacion || null,
    compartirConProyecto: !!data.compartirConProyecto,
    updatedAt: serverTimestamp()
  };
  if (id) {
    await updateDoc(docRefFor(uid, 'notasSap', id, projectId), payload);
    return id;
  }
  const ref = await addDoc(coleccionRef(uid, 'notasSap', projectId), { ...payload, createdAt: serverTimestamp() });
  return ref.id;
};

export const deleteNotaSap = (uid: string, id: string, projectId: string | null) => deleteItem(uid, 'notasSap', id, projectId);

// ── Notas & Pendientes ───────────────────────────────────────────────────
export const getTareas = (uid: string, projectIds: (string | null)[]) => getItemsMulti<TareaData>(uid, 'tareas', projectIds);

// Antes recibía solo `titulo` (string) — ahora recibe el objeto completo
// del modal de creación (ver TareaFormModal.tsx). Único call site interno
// (TareasSection.tsx), no hay otros consumidores que romper.
export const crearTarea = async (uid: string, data: TareaData, projectId: string | null): Promise<void> => {
  if (!data.titulo.trim()) throw new Error("El título de la tarea es obligatorio.");
  await addDoc(coleccionRef(uid, 'tareas', projectId), {
    titulo: data.titulo.trim(),
    completada: false,
    prioridad: data.prioridad || 'Media',
    fechaLimite: data.fechaLimite || null,
    categoria: data.categoria || 'Otro',
    notas: (data.notas || '').trim(),
    createdAt: serverTimestamp()
  });
};

export const updateTarea = async (uid: string, id: string, data: TareaData, projectId: string | null): Promise<void> => {
  if (!data.titulo.trim()) throw new Error("El título de la tarea es obligatorio.");
  await updateDoc(docRefFor(uid, 'tareas', id, projectId), {
    titulo: data.titulo.trim(),
    prioridad: data.prioridad || 'Media',
    fechaLimite: data.fechaLimite || null,
    categoria: data.categoria || 'Otro',
    notas: (data.notas || '').trim()
  });
};

export const toggleTarea = async (uid: string, id: string, completada: boolean, projectId: string | null): Promise<void> => {
  await updateDoc(docRefFor(uid, 'tareas', id, projectId), { completada });
};

export const deleteTarea = (uid: string, id: string, projectId: string | null) => deleteItem(uid, 'tareas', id, projectId);

// ── Directorio de Contactos ──────────────────────────────────────────────
export const getContactos = (uid: string, projectIds: (string | null)[]) => getItemsMulti<ContactoData>(uid, 'contactos', projectIds);

export const saveContacto = async (uid: string, id: string | null, data: ContactoData, projectId: string | null): Promise<void> => {
  if (!data.nombreCompleto.trim()) throw new Error("El nombre completo es obligatorio.");
  const payload = {
    nombreCompleto: data.nombreCompleto.trim(),
    empresa: data.empresa.trim(),
    rol: data.rol.trim(),
    email: data.email.trim(),
    telefono: data.telefono.trim(),
    notas: data.notas.trim(),
    updatedAt: serverTimestamp()
  };
  if (id) {
    await updateDoc(docRefFor(uid, 'contactos', id, projectId), payload);
  } else {
    await addDoc(coleccionRef(uid, 'contactos', projectId), { ...payload, createdAt: serverTimestamp() });
  }
};

export const deleteContacto = (uid: string, id: string, projectId: string | null) => deleteItem(uid, 'contactos', id, projectId);

// ── Archivos ──────────────────────────────────────────────────────────────
export const getArchivos = (uid: string, projectIds: (string | null)[]) =>
  getItemsMulti<Omit<Archivo, 'id' | 'projectId'>>(uid, 'archivos', projectIds);

/**
 * A diferencia del resto de "Mi Espacio", esto sube un archivo a Storage
 * ANTES de escribir el doc — mismo patrón de 2 pasos que
 * casoPruebaService.createCasoPrueba: se pre-genera el id del doc (`doc()`
 * sin `addDoc`) para poder armar la ruta de Storage
 * `usuarios/{uid}/archivos/{id}/...` de una sola vez, y recién después se
 * hace `setDoc` con la URL ya resuelta.
 */
export const createArchivo = async (uid: string, nombre: string, descripcion: string, file: File, projectId: string | null): Promise<void> => {
  if (!file) throw new Error("Seleccioná un archivo.");
  const ref = doc(coleccionRef(uid, 'archivos', projectId));
  const archivo = await uploadAttachment(`usuarios/${uid}/archivos/${ref.id}`, file);
  await setDoc(ref, {
    nombre: (nombre || file.name).trim(),
    descripcion: descripcion.trim(),
    archivo,
    createdAt: serverTimestamp()
  });
};

/** Renombrar/redescribir un archivo ya subido — el archivo en sí no se reemplaza. */
export const renameArchivo = async (uid: string, id: string, nombre: string, descripcion: string, projectId: string | null): Promise<void> => {
  if (!nombre.trim()) throw new Error("El nombre es obligatorio.");
  await updateDoc(docRefFor(uid, 'archivos', id, projectId), { nombre: nombre.trim(), descripcion: descripcion.trim() });
};

export const deleteArchivo = (uid: string, id: string, projectId: string | null) => deleteItem(uid, 'archivos', id, projectId);

// Re-exportados por conveniencia para los componentes de sección.
export type { Snippet, NotaSap, Tarea, Contacto, Archivo };
